import {
  type AvailabilityInfo,
  type AvailabilityInfoDTO,
  transformAvailability,
} from "~/domains/product/api/pna/AvailabilityInfo";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { ApiCallPriority, apiGET, apiPOST } from "~/app/api/apiCallsService";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { BATCHING_AVAILABILITY_ENABLED, BATCHING_INTERVAL, BATCHING_PNA_BATCHES } from "~/utils/constants";
import { BatchSizeProvider } from "~/app/api/batchSizeProvider";
import { toMapFn } from "~/common/utils/reducers";

export interface AvailabilityApiData {
  productCode: string;
  packCode?: string;
}

interface AvailabilityQueueElement {
  resolve: (data: AvailabilityInfo) => void;
  reject: (data: unknown) => void;
  data: AvailabilityApiData;
}

export function generateAvailabilityApiKey(data: AvailabilityApiData) {
  return `${data.productCode}-${data.packCode}`;
}

const A_CACHE: Dict<Promise<AvailabilityInfo>> = {};
const queuedACalls: AvailabilityQueueElement[] = [];
let aGroupingScheduled: boolean = false;

export function requestAvailabilityDataSingle(data: AvailabilityApiData) {
  const cacheKey = generateAvailabilityApiKey(data);
  let cacheEntry = A_CACHE[cacheKey];

  if (!cacheEntry) {
    const queueElement: Partial<AvailabilityQueueElement> = { data };
    cacheEntry = new Promise((resolve, reject) => {
      queueElement.resolve = resolve;
      queueElement.reject = reject;
    });
    cacheEntry.finally(() => {
      delete A_CACHE[cacheKey];
    });
    const finalizedQueueElement = queueElement as AvailabilityQueueElement;
    queuedACalls.push(finalizedQueueElement);
    A_CACHE[cacheKey] = cacheEntry;

    if (!aGroupingScheduled) {
      aGroupingScheduled = true;
      window.setTimeout(dispatchApiCall, BATCHING_INTERVAL);
    }
  }

  return cacheEntry;
}

const batchSizeProvider = new BatchSizeProvider(BATCHING_PNA_BATCHES);

function dispatchApiCall() {
  const itemsToCall = queuedACalls.slice(0, batchSizeProvider.getBatchSize());
  queuedACalls.splice(0, itemsToCall.length);

  aGroupingScheduled = false;

  if (itemsToCall.length === 0) {
    return;
  }

  if (!BATCHING_AVAILABILITY_ENABLED) {
    for (const item of itemsToCall) {
      const promise = apiFetchAvailabilitySingle(item.data.productCode, item.data.packCode);
      promise.then(item.resolve, item.reject);
    }
  } else {
    const promise = apiFetchAvailabilityMultiple(itemsToCall.map((item) => item.data));
    promise.then(
      (data) =>
        itemsToCall.forEach((item) => {
          const fetchedData = data[item.data.productCode];
          item.resolve(fetchedData);
        }),
      (error) => itemsToCall.forEach((item) => item.reject(error)),
    );
  }

  if (queuedACalls.length > 0) {
    aGroupingScheduled = true;
    window.setTimeout(dispatchApiCall, BATCHING_INTERVAL);
  }
}

async function apiFetchAvailabilitySingle(productCode: string, packCode?: string): Promise<AvailabilityInfo> {
  const singleApiUrl = resolveApiUrl(`/pna/getAvailability/${productCode}` + (packCode ? `?packCode=${packCode}` : ""));

  const response = await apiGET(
    singleApiUrl,
    {
      cache: "no-cache",
    },
    ApiCallPriority.LOW,
  );

  if (!response.ok) {
    throw new Error("Error fetching data");
  }

  const dto: AvailabilityInfoDTO = await response.json();
  return transformAvailability(dto);
}

const multiApiUrl = resolveApiUrl("/pna/getAvailabilityList");

async function apiFetchAvailabilityMultiple(data: AvailabilityApiData[]): Promise<Record<string, AvailabilityInfo>> {
  const body = data.map((item) => ({
    code: item.productCode,
    packCode: item.packCode,
    quantity: 1,
  }));

  const response = await apiPOST(
    multiApiUrl,
    {
      body: JSON.stringify({ products: body }),
      cache: "no-cache",
      headers: {
        "content-type": "application/json",
        CSRFToken: getCsrfValue(),
      },
    },
    ApiCallPriority.LOW,
  );

  if (!response.ok) {
    throw new Error("Error fetching data");
  }
  const dto: AvailabilityInfoDTO[] = await response.json();
  const transformedData = dto.map(transformAvailability);

  // noinspection UnnecessaryLocalVariableJS
  const record = toMapFn(transformedData, (item) => item.code);
  return record;
}
